import React, {Component} from 'react';
import './AuctionPartner.scss';
import ContentTemplate from "../ContentTemplate/ContentTemplate";
import CodeSnippetTemplate from "../CodeSnippetTemplate/CodeSnippetTemplate";
import CodeSnippet from "../../model/CodeSnippet";
import {CodeSnippetLanguages} from "../CodeSnippetTemplate/CodeSnippetLanguages";
import HttpRedirectFlow from "../../assets/images/HTTP-Redirect-Flow.png";
import SellerPaidBatch from "../../assets/images/seller-paid-batch.png";
import Constants from "../Constants";
import imgDealerSequence from "../../assets/images/dealer-report-link-sequence.png";
import {HashLink as Link} from "react-router-hash-link";
import SellerPaid_1 from "../../assets/images/seller-paid-1.png";
import imgSnapshot from "../../assets/images/snapshot-dealer.png";
import imgSorry from "../../assets/images/sorry-snapshot.png";
import CollapsibleContent from "../CollapsibleContent/CollapsibleContent";
import PartnerPortalApplications from "../../assets/images/partner-portal-applications.png";


interface State {

}

interface Props {

}


export default class AuctionPartner extends Component<Props, State> {

    private auctionPartnerNavLinks: Map<string, string> = new Map([
        ["#dealer-reports", "Dealer Reports"],
        ["#carfax-link-auth-flow", "CARFAX Report Link"],
        ["#snapshot", "Snapshot"],
        ["#vehicle-information", "Vehicle Information"],
    ]);

    private sellerPaidNavLinks: Map<string, string> = new Map([
        ["#seller-paid-reports", "Auction Partner Seller Paid Reports"],
        ["#api-schema", "API Schema"],
        ["#seller-paid-prerequisites", "Prerequisites"],
        ["#step-1-access-token", "Step 1 - Access Token"],
        ["#step-2-graphql-query", "Step 2 - GraphQL Query"],
        ["#step-3-send-request", "Step 3 - Send Request"],
        ["#cc-carfax-report-links", "CARFAX Report Link"],
        ["#seller-paid-snapshot", "Snapshot"],
        ["#cc-vehicle-information", "Vehicle Information"]
    ]);

    private auctionAlertNavLinks: Map<string, string> = new Map([
        ["#overview", "Overview"],
        ["#api-schema-auction-alert","API Schema"],
        ["#prerequisites-auction-alert","Prerequisites"],
        ["#authentication", " Authentication"],
        ["#step-1-access-token-auction-alert","Step 1 - Access Token"],
        ["#step-2-graphql-query-auction-alert","Step 2 - GraphQL Query"],
        ["#step-3-send-request-auction-alert","Step 3 - Send Request"],
        ["#response-format", "Response Format"],
        ["#auction-alert-response", "Auction Alert Response"],
        ["#decode", "Decode"],
        ["#error-messages", "Error Messages"],
        ["#last-reported-odometer-reading", "Last Reported Odometer Reading"],
        ["#request-info", "Request Info"],
        ["#additional-support", "Additional Support"]
    ]);


    private navLinks: Map<string, Map<string, string>> = new Map<string, Map<string, string>>([
        ["Auction Partner", this.auctionPartnerNavLinks],
        ["Auction Partner Seller Paid", this.sellerPaidNavLinks],
        ["Auction Alert", this.auctionAlertNavLinks]
    ]);

    public render() {
        return (
            <div className={"auction-partner-seller-paid"}>
                <ContentTemplate
                    pageTitle={"Auction Partner"}
                    navlinksMap={this.navLinks}
                    content={this.getContent()}
                />
            </div>
        )
    }

    private getContent(): JSX.Element {
        return (
            <div className="content-container auction-partner-page">
                <h1 id={"dealer-reports"}>Auction Partner</h1>
                <section>
                    <h3 id={"dealer-reports"}>Dealer Reports</h3>
                    <p>
                        The Auction Partner path of CARFAX Connect is used by dealer facing auction partners to enable
                        CARFAX Report purchases by authenticated users.
                        The information here provides details on how to retrieve a Dealer Report and feature CARFAX
                        Snapshot.
                    </p>
                    <p>
                        To enable the integration, users must first link their account in the
                        partner application to their CARFAX account. Connect uses&nbsp;
                        <a href={Constants.AUTHENTICATION_PAGE_URL + "#authorization-code-flow"}>
                            OAuth 2 authorization code flow{" "}
                        </a>
                        to let users authorize the partner to access CARFAX Connect on their
                        behalf. When Connect processes a request it identifies the dealer
                        based on the access token sent in the request header. You cannot mix
                        different dealers in a batch request since each request has only one{" "}
                        <code>Authorization</code> header.
                    </p>
                    <p>
                        The rules for provisioning CARFAX Reports depend on the dealer's
                        subscription type:
                    </p>
                    <ul>
                        <li>
                            <strong>CARFAX Advantage</strong> dealers receive unlimited CARFAX
                            Reports. They can access a report for any VIN without a prior
                            explicit purchase.
                        </li>
                        <li>
                            <strong>Pay-per-VIN</strong> dealers can only access reports for
                            VINs in their CARFAX inventory. An explicit purchase request for a
                            VIN is required to add a CARFAX Report to the dealer's inventory.
                        </li>
                        <li>Both dealer types must have an active CARFAX account.</li>
                    </ul>
                    <p>
                        As an Auction Partner you do not need to know which type of subscription a dealer uses or
                        whether a given VIN is in a dealer's CARFAX inventory. In this case, all dealer users will
                        simply click a CARFAX button or "View CARFAX Report" link to run the report. To enable this you
                        will use the mutation noted below:
                    </p>
                    <ul>
                        <li>
                            <strong>
                                Mutation <code>purchaseDealerReportIfRequired</code>
                            </strong>{" "}
                            &mdash; Always returns the requested report. If required by the provisioning rules, the report is purchased to make it available.
                            <ul>
                                <li>
                                    <strong>Advantage</strong> dealers always get the report, the same as with the <strong>Query dealerReport</strong> used in non-auction dealer facing tools.
                                </li>
                                <li>
                                    If the request is made for a <strong>pay-per-VIN</strong> dealer
                                    and the VIN is not already in inventory, it will be added and
                                    the dealer will be charged. If the VIN is already in inventory,
                                    the dealer will not be charged again.
                                </li>
                                <li>
                                    The purchase mutation should only be used when the user
                                    performed an action signalling the intent to purchase, such as
                                    clicking a CARFAX Report link.
                                </li>
                            </ul>
                        </li>
                    </ul>
                    <p>
                        If a dealer's CARFAX account is inactive, both the query and mutation
                        will return an error with <code>errorCode</code>{" "}
                        <code>INVALID_ACCOUNT</code>.{" "}
                    </p>
                </section>
                <hr/>

                {/*CARFAX Report Links*/}
                <section>
                    <h3 id="carfax-link-auth-flow">CARFAX Report Links - Auth Code Flow</h3>
                    <p>
                        All CARFAX Report links can be displayed with the same text or icon
                        regardless of the dealer's subscription type or the VIN's inventory
                        presence. When pay-per-VIN dealers authorize a partner application to
                        use CARFAX they grant permission for report purchases to be made
                        automatically whenever they specifically click on a CARFAX Report.
                        Therefore you can always use the{" "}
                        <code>purchaseDealerReportIfRequired</code>
                        mutation to obtain the report link. Connect will automatically perform
                        the purchase if required before the report link is returned.
                    </p>
                    <p>
                        CARFAX Report links expire after 7 days. Therefore you should not cache them in
                        advance. Rather, call Connect when the user clicks the report link and
                        redirect the browser to the report URL provided. You can use
                        JavaScript or an actual HTTP redirect from your back end to send the
                        browser to the report URL. Whatever mechanism you use, ensure the
                        redirect is not cached longer than the URL expiration time. If using
                        HTTP redirects, we recommend using status <code>303 See Other</code>{" "}
                        because it will not be cached and forces the browser to make a{" "}
                        <code>GET</code> request to the new URL regardless of the method used
                        in the initial back end request.
                    </p>
                    <p>
                        Sequence for HTTP redirect:
                        <br/>
                        <img
                            alt="Sequence diagram for HTTP redirect for CARFAX link"
                            src={imgDealerSequence}
                        />
                    </p>
                    <p>
                        Example mutation:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `mutation ($vin: Vin!) {
    purchaseDealerReportIfRequired(vin: $vin) {
        dealerReport {
            carfaxLink {
                url
            }
            expiresAt { 
                rfc 
            }
        }
    }
}`
                            )
                        ]}/>
                    </p>
                    <p>
                        Variables:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify({vin: "19UUA5666YA002236"}, null, 4)
                            )
                        ]}/>
                    </p>
                    <p>
                        Response:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify(
                                    {
                                        data: {
                                            purchaseDealerReportIfRequired: {
                                                dealerReport: {
                                                    carfaxLink: {
                                                        url: "https://connect.carfax.com/dir?key=oRM754b111ni",
                                                    },
                                                    expiresAt: {
                                                        rfc: "2020-02-20T16:35:55.333Z",
                                                    }
                                                },
                                            },
                                        },
                                    },
                                    null,
                                    4
                                )
                            )
                        ]}/>
                    </p>
                </section>
                <hr/>

                {/*Snapshot*/}
                <section>
                    <h3 id="snapshot">Snapshot</h3>
                    <p>
                        Snapshot is a simple html file generated for the client to give a
                        'snapshot' of key vehicle history information. When added to an
                        inventory management tool, Snapshot offers these benefits:
                    </p>
                    <ul>
                        <li>
                            For all vehicles already featuring a Dealer Instant CARFAX Report,
                            dealers may now see the same data found in the header of the
                            report at all places where they engage with the vehicle in your
                            application.
                        </li>
                        <li>
                            With just a hover over the CARFAX logo, dealers can quickly see
                            the key vehicle history points for the given vehicle, allowing
                            them to make timely inventory management decisions.
                        </li>
                        <li>
                            Dealers can reference the CARFAX History-Based Value for their
                            vehicles if your agreement terms provide for this added benefit.{" "}
                        </li>
                    </ul>
                    <img src={imgSnapshot} alt="speSnapshot Example" className={"img-fluid"} width="250px"/>
                    <p>
                        Unlike the legacy Snapshot, the new Snapshot URL has a
                        Snapshot key which hides the VIN and dealer information. Advantage and
                        Pay Per VIN dealers can both request Snapshot. Advantage dealers will
                        always receive Snapshot but Pay Per VIN dealers may only view it if
                        the VIN is already in their inventory. Two approaches can be used to
                        request Snapshot:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `query ($vin: Vin!) {
    dealerReport(vin: $vin) {
        snapshotKey
    }
}`
                            )
                        ]}/>
                    </p>
                    <p>
                        Variables:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify({vin: "JN8AF5MR5CT116133"}, null, 4)
                            )
                        ]}/>
                    </p>
                    <p>
                        Sample response:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify(
                                    {
                                        data: {
                                            dealerReport: {
                                                snapshotKey:
                                                    "p5lI0fc6F52ATD-ayqZRUze_I30-6NQVyEahpAADvz4fcqX1kP6rNRvuA2JJBhC1ZDrE1dgeV7M1pBTGCWG3rVJDfY2aWSUvAmp0IpeC6tnf1oxHutKkb3s0jeUmrevT_GnG43aWAbyOlTlueofoE8fF",
                                            },
                                        },
                                    },
                                    null,
                                    4
                                )
                            )
                        ]}/>
                    </p>
                    <p>
                        Purchasing a Dealer Report:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `mutation ($vin: Vin!) {
    purchaseDealerReportIfRequired(vin: $vin) {
        dealerReport {
            snapshotKey
        }
    }
}`
                            )
                        ]}/>
                    </p>
                    <p>
                        Variables:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify({vin: "JN8AF5MR5CT116133"}, null, 4)
                            )
                        ]}/>
                    </p>
                    <p>
                        Sample Response:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify(
                                    {
                                        data: {
                                            purchaseDealerReportIfRequired: {
                                                dealerReport: {
                                                    snapshotKey:
                                                        "8D7mVwqG3YTtN8KZuSVCP7niVLsUdH2Vs32jFn9qEUYbssf8olRkBuEe8DtoZaNkp3BXJL4ADy8th5O6gJSxQbM7Q7zC4tFJOq7t45KZdHwEbed2Vu7EppCSgupN5jBmB_V4ZoCzhBA7IA_P1NNYA__k",
                                                },
                                            },
                                        },
                                    },
                                    null,
                                    4
                                )
                            )
                        ]}/>
                    </p>
                    <p>
                        Information such as <code>{`vin`}</code>, <code>{`userId`}</code> and{" "}
                        <code>{`dealerCompCode`}</code>
                        are embedded in the <code>{`snapshotKey`}</code>; which can be used to
                        render the Snapshot data through the dealer Snapshot endpoint. The key
                        is used to ensure that the requests being made against our server are
                        authorized and can also be injected in an img or anchor tag to show
                        the Snapshot html.
                    </p>
                    <h4 id="including-snapshotjs">Including Snapshot.js</h4>
                    <p>
                        When implementing Snapshot, you'll need to use our hosted JavaScript
                        file:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                '<script type="text/javascript" src="https://snapshot.carfax.com/latest/dealer-snapshot.js"></script>'
                            )
                        ]}/>
                    </p>
                    <p>
                        Generally, it should be included right before you close out your body
                        tag in your HTML document.
                    </p>
                    <h4 id="dealer-snapshot-hover-over">Dealer Snapshot Hover-Over</h4>
                    <p>
                        On a desktop application, we recognize that most existing promotions
                        will show simply a CARFAX logo or icon. Hovering over the logo/icon
                        will cause the Snapshot to render just to the side of the logo/icon.
                        Clicking on the logo/icon should open the dealer report directly.{" "}
                    </p>
                    <p>
                        Applying the bindings for the Snapshot hover-over is as easy as adding
                        only two attributes on your html element that you would like to display
                        Snapshot, when hovered over. The elements are as follows:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                'class="carfax-dealer-snapshot-hover" data-snapshotkey="SnapshotKey"'
                            )
                        ]}/>
                    </p>
                    <p>
                        SnapshotKey = The key you will get back from the
                        Connect Dealer Report Response
                    </p>
                    <p>
                        For example, an Image tag that shows the report when hovered over
                        would follow this pattern.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                '<img src="photo.jpg" class="carfax-dealer-snapshot-hover" data-snapshotkey="SnapshotKey"/>'
                            )
                        ]}/>
                    </p>
                    <h4 id="embedded-dealer-snapshot">Embedded Dealer Snapshot</h4>
                    <p>
                        The Dealer Snapshot may also be embedded on any page of your
                        application for easy presentation of the CARFAX vehicle history.
                        <br/>
                        The embedded Snapshot displays just as the hover-over version,
                        however, it’s not triggered by hovering over an element, and instead,
                        it’s added to the page as it loads. Just like the hover-over version
                        of Snapshot, the embedded version also allows dealers to click “View
                        the CARFAX Report” in order to see the full report.
                    </p>
                    <p>
                        The class for the embedded Snapshot is “carfax-snapshot-embedded”. You
                        will want to include the same JavaScript library whether using the
                        embedded or pop-up version of Snapshot on a page. We also recommend
                        using a <code>{`<div>`}</code> element for the embedded Snapshot to
                        ensure everything is styled appropriately.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                'class="carfax-dealer-snapshot-embedded" data-snapshotkey="SnapshotKey"'
                            )
                        ]}/>
                    </p>
                    <p>
                        SnapshotKey = The key you will get back from the
                        Connect Dealer Report Response
                    </p>
                    <p>
                        For example, an Image tag that shows the report when hovered over
                        would follow this pattern.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                '<div class="carfax-dealer-snapshot-embedded" data-snapshotkey="SnapshotKey"></div>'
                            )
                        ]}/>
                    </p>
                    <h4 id={"refreshing snapshot"}>Refreshing Snapshot</h4>
                    <p>
                        If you have vin data loaded after the page load has already happened, then you should
                        reload the Snapshot.js file so that any newly added snapshots show up correctly.
                        In order to do so, please trigger the 'refreshSnapshotEvent' after the Connect data has
                        finished loading. This action will enable the proper viewing of all the snapshots on the page.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JAVASCRIPT,
                                "JavaScript",
                                `const refreshSnapshotEvent = new Event('refreshCarfaxSnapshotData');

document.dispatchEvent(refreshSnapshotEvent);`
                            )
                        ]}/>
                    </p>
                    <h4 id="specs">Specs</h4>
                    <p>
                        Snapshot is built to be resized responsively but we do have the
                        following min/max sizes that we suggest you use when rendering
                        Snapshot:
                    </p>
                    <ul>
                        <li>
                            Minimum width = 250 pixels
                        </li>
                        <li>
                            Maximum width = 350 pixels (a user may enlarge it beyond this but
                            the rows will simply stretch)
                        </li>
                        <li>
                            Maximum height = 518 pixels if the Year/Make/Model shows on three
                            lines
                        </li>
                    </ul>
                    <p>
                        Please note that the final implementation of Snapshot in your
                        application shall be mutually agreed to and approved by your CARFAX
                        account manager prior to launch. We encourage you to share screen
                        shots and/or provide beta access to your application to facilitate
                        this process.
                    </p>
                    <h4 id="sorry-message">Sorry Message</h4>
                    <p>
                        Should the report be unavailable, the service be down or the request
                        not properly authenticated, the dealer will receive a “sorry” message
                        like the one below.
                        <br/>
                        <img src={imgSorry} alt="Sorry Page" width="300" height="390"/>
                    </p>
                </section>
                <hr/>

                {/*Vehicle Information*/}
                <section>
                    <h3 id={"vehicle-information"}>Vehicle Information</h3>
                    <p>
                        In GraphQL, the data for the queries is returned in the same order that it was requested.
                        Although there may be Use Cases where the final state of the data is not in the same order.
                        In that case, there will need to be some sort of explicit mapping for the requested data with
                        its response.
                        CARFAX Connect supports the <code>Vehicle</code> Type which can be used to get back basic
                        Vehicle information,
                        such as the <code>vin</code> to map the requested data to its response:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `query {
    dealerReport(vin: "VIN123") {
        carfaxLink {
            url
        }
   
        vehicle {
            vin
        }
    }
}`
                            )
                        ]}/>
                    </p>


                    <p>
                        Alternatively, <Link to={`${Constants.SEND_QUERIES_PAGE_URL}#graphql-aliases`}>GraphQL
                        Aliases</Link> can
                        be used to achieve this mapping.
                    </p>
                    <p>
                        Please note that the VIN is also returned in cases when an error occurs to aid in diagnosing the
                        given issue.
                        See <Link to={`${Constants.ERROR_HANDLING_PAGE_URL}`}>Error Handling</Link> for more information
                        and
                        examples of how the VIN is returned with errors.
                    </p>
                </section>
                <hr/>

                {/*Auction Seller Paid - CARFAX Report Links*/}
                <section>
                    <h3 id="seller-paid-reports">Auction Partner Seller Paid Reports</h3>
                    <p>
                        The Auction Partner Seller Paid Report option via Connect allows CARFAX Reports to be
                        displayed on select consignor inventory on an auction site. These CARFAX Reports are able to be
                        viewed by any dealer or user of the auction site as a benefit to help sell the inventory. Some
                        guidelines for how this option is to be used:

                        <ul>
                            <li>
                                The partner integrates the CARFAX Connect API into the application that will be used by
                                dealers.
                            </li>
                            <li>The API should be associated with each specific VIN listing with a corresponding "View
                                CARFAX Report" link and CARFAX logo or other agreed upon graphic.
                                See the <Link to={Constants.DEALER_PAGE_URL + "#carfax-branding"}>CARFAX
                                    Branding </Link>section for details.
                            </li>
                            <li>When the link/logo is clicked, the partner’s application sends a GraphQL request to
                                CARFAX with all the necessary parameters and headers.
                            </li>
                            <li>CARFAX initiates the logic and then returns the report URL that displays the CARFAX
                                Report in a new tab or window.
                            </li>
                        </ul>
                    </p>
                </section>
                <hr/>

                {/*Seller Paid - API Schema*/}
                <section>
                    <h3 id={"api-schema"}>Seller Paid - API Schema</h3>
                    <p>
                        For the purposes of this guide, the following Schema will be used:
                        <br/>
                        <CollapsibleContent content={
                            <CodeSnippetTemplate codeSnippets={[
                                new CodeSnippet(
                                    CodeSnippetLanguages.GRAPHQL,
                                    "GraphQL",
                                    `extend type Query{
    auctionSellerPaidDealerReport(vin: Vin): DealerReport
}
type DealerReport{
    # Link to the CARFAX report
    carfaxLink: DealerReportLink
    
    # Key used to render a CARFAX snapshot
    snapshotKey: String
 
    # Expiration for the CARFAX report
    expiresAt: Timestamp
    
    # Basic information about the vehicle
    vehicle: Vehicle
}

# Link to the CARFAX report
type DealerReportLink {
    url: Url!
}

# Vehicle Information
type Vehicle {
    vin: Vin
}

# Instant in time (not local)
type Timestamp {
    # UNIX timestamp; seconds elapsed since the UNIX epoch, 1970-01-01T00:00:00Z, neglecting leap seconds
    unix: Seconds!
 
    # Milliseconds elapsed since the UNIX epoch, 1970-01-01T00:00:00Z, neglecting leap seconds
    unixMillis: Milliseconds!
 
    # Date-time string in RFC 3339/ISO 8601 format, such as '2019-02-01T13:22:33Z'
    rfc: RfcTimestamp!
}

# Vehicle Identification Number; 17 uppercase alphanumeric characters
scalar Vin
 
# An absolute URL, with encoding applied as needed
scalar Url
 
# RFC 3339 date & time (an ISO 8601 profile) such as '2019-02-01T13:22:33Z'
scalar RfcTimestamp
 
# Integer in the JavaScript safe range -(2^53 - 1) to 2^53 - 1
scalar IntJs
 
# Seconds represented as an IntJs scalar
scalar Seconds
 
# Milliseconds represented as an IntJs scalar
scalar Milliseconds
 
}

`
                                )
                            ]}/>
                        }/>
                    </p>
                </section>
                <hr/>

                {/*Seller Paid - Prerequisites*/}
                <section>
                    <h3 id={"seller-paid-prerequisites"}>Seller Paid - Prerequisites</h3>
                    <p>
                        Before you can authenticate and start running queries on CARFAX Connect, you must have
                        a <strong>Client Credentials Application</strong> set up in the Partner Portal.
                        Log in to the CARFAX Partner Portal using your developer credentials.
                        You will need to retrieve the Client ID and Client Secret of a Client Credentials application.
                        <ul>
                            <li>
                                If you do not have a Client Credentials application assigned, you can create one using
                                the New Application button.
                                A popup will appear, allowing you to name the new application.
                            </li>
                        </ul>
                    </p>
                    <p className={"text-center"}>
                        <img
                            src={SellerPaid_1}
                            width="647px"
                            alt="Partner Portal: Applications Screen"
                        />
                    </p>
                    <ul>
                        <li>
                            Upon clicking Create, the new application will appear in your list of applications.
                        </li>
                    </ul>
                </section>
                <hr/>

                {/*Seller Paid - Step 1*/}
                <section>
                    <h3 id={"step-1-access-token"}>Step 1 - Get an Access Token</h3>
                    <p>
                        CARFAX Connect follows the OAuth 2.0 authentication process. You'll be using the Client
                        Credentials flow to get an Access Token.
                        Use your Client ID and Client Secret to retrieve an access token with an HTTP POST request
                        to <i>
                        https://auth.carfax.com/oauth/token</i>. You can use the following cURL to request an access
                        token.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(CodeSnippetLanguages.MARKUP, 'cURL',
                                `curl -XPOST \\
    -H "Content-type: application/json" \\
    -d '{"client_id":"YOUR_CLIENT_ID","client_secret":"YOUR_CLIENT_SECRET","audience":"https://connect.carfax.com","grant_type":"client_credentials"}' 'https://auth.carfax.com/oauth/token'
`)
                        ]}/>
                    </p>
                    <p>The response will be similar to the response below. As you can see your access token is
                        under <i>access_token</i> field.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(CodeSnippetLanguages.JSON, 'JSON',
                                JSON.stringify(
                                    {
                                        "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9....",
                                        "scope": "read:client_grants create:client_grants ... ",
                                        "expires_in": 86400,
                                        "token_type": "Bearer"
                                    }, null, 4
                                ))
                        ]}/>
                    </p>
                    <p>
                        <strong>IMPORTANT</strong>: An access token once obtained is valid for 24 hours and should be reused until it expires.
                        Please do not fetch a new access token with each request. If coded properly we would expect to see one refresh per day if calling from a single machine or perhaps as many as 10 if using distributed machines to call Connect.
                        The expiration is identified via expires_in which is in seconds. Be sure to obtain a fresh token sufficiently far in advance to allow for network delays and clock skew.
                    </p>
                </section>
                <hr/>

                {/*Seller Paid - Step 2*/}
                <section>
                    <h3 id={"step-2-graphql-query"}>Step 2 - Prepare the GraphQL Query</h3>
                    <p>

                        For the purposes of this guide, we will use the following GraphQL query:

                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `query{
  auctionSellerPaidDealerReport(vin: "2FTRX18L62CA68371") {
    carfaxLink {
      url
    }
  }
}
`
                            )
                        ]}/>
                        <p>
                            Convert your GraphQL to a JSON object. You can also use commas(,) or spaces to separate the
                            different types in the query instead of the newline character(\n).
                            GraphQL is very flexible when it comes to how you want to format the query if readability is
                            important:
                        </p>
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(CodeSnippetLanguages.JSON, 'JSON',
                                JSON.stringify({
                                    "query": "query{auctionSellerPaidDealerReport(vin:\"2FTRX18L62CA68371\") { carfaxLink { url } } }"
                                }, null, 4))
                        ]}/>
                        For more information on sending GraphQL queries, and what type of queries CARFAX Connect
                        supports, please refer to&nbsp;
                        <Link to={Constants.SEND_QUERIES_PAGE_URL}>Sending Queries page</Link>.
                    </p>

                </section>
                <hr/>

                {/*Seller Paid - Step 3*/}
                <section>
                    <h3 id={"step-3-send-request"}>Step 3 - Sending a request to CARFAX Connect</h3>
                    Now you are ready to make a request to CARFAX Connect. To start, prepare a new HTTP POST request to
                    the <i> http://connect.carfax.com/v1/graphql </i>.
                    Paste the access token from the first step into the header of the HTTP request.
                    Don't forget to put "Bearer" and then a space before the access token.
                    In the body of the request to CARFAX Connect, write your GraphQL query in json format from the
                    previous step.
                    Example below shows the whole request for querying a CARFAX link in cUrl format:
                    <CodeSnippetTemplate codeSnippets={[
                        new CodeSnippet(CodeSnippetLanguages.MARKUP, 'cURL',
                            `curl --request POST \\
  --url https://connect.carfax.com/v1/graphql \\
  --header 'authorization: Bearer <YOUR_ACCESS_TOKEN_HERE>' \\
  --header 'content-type: application/json' \\
  --data '{"query":"query{auctionSellerPaidDealerReport(vin:\\"JM3KE4CY0G0797189\\") { carfaxLink { url } } }"}'`)
                    ]}/>
                    <p>
                        If you use the exact same query as the example above, the response you receive would be like:

                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `
{
  "data": {
    "auctionSellerPaidDealerReport": {
      "carfaxLink": {
        "url": "REPORT_URL_HERE"
      }
    }
  }
}
`
                            )
                        ]}/>
                    </p>
                    <p>Now you are all set to use your client credentials and query connect to receive report for
                        auction partner seller paid.
                        For more information about the authentication, check out the <Link
                            to={Constants.SEND_QUERIES_PAGE_URL}>Client Credentials authentication flow</Link>.
                    </p>
                </section>
                <hr/>


                {/*Seller Paid - Carfax Links*/}
                <section>
                    <h3 id="cc-carfax-report-links">CARFAX Report Links - Client Credentials Flow</h3>
                    <p>
                        CARFAX Report links expire after 7 days, so keep that in mind if you choose to cache them.
                        If you decide not to cache CARFAX links, you should call Connect when the user clicks the report
                        link and redirect the browser to the report URL provided.
                        You can use JavaScript or an actual HTTP redirect from your back end to send the browser to the
                        report URL. Whatever mechanism you use, ensure the redirect is not cached longer than the URL
                        expiration time.
                        If using HTTP redirects, we recommend using status 303 See Other because it will not be cached
                        and forces the browser to make a GET request to the new URL regardless of the method used in the
                        initial back end request.
                        <br/>
                        Sequence for HTTP redirect:

                        <p className={"text-center"}>
                            <img
                                src={HttpRedirectFlow}
                                width="730px"
                                alt="Partner Portal: Applications Screen"
                            />
                        </p>

                        <p>

                            You do not need to query all the fields in the queries that you send. GraphQL allows you to
                            choose only the fields (or data points) that you and receive the reponse in json format.
                            As an example consider the query below:

                            <CodeSnippetTemplate codeSnippets={[
                                new CodeSnippet(
                                    CodeSnippetLanguages.GRAPHQL,
                                    "GraphQL",
                                    `
query {
    auctionPartnerSellerPaid($vin: Vin!) {
        carfaxLink{
            url
        }
        expiresAt {
            unix
            unixMillis
            rfc
        }
    }
}`
                                )
                            ]}/>
                            In order to be more secure against injection attacks and to keep your query more secure, it
                            is a good practice to pass the input parameters
                            inside the <strong>Variables</strong> field instead of putting the inputs directly inside
                            the query:
                            <CodeSnippetTemplate codeSnippets={[
                                new CodeSnippet(
                                    CodeSnippetLanguages.GRAPHQL,
                                    "GraphQL",
                                    `{
    "vin": "2FTRX18L62CA68371"
}`
                                )
                            ]}/>

                            The response for the above query:
                            <CodeSnippetTemplate codeSnippets={[
                                new CodeSnippet(
                                    CodeSnippetLanguages.GRAPHQL,
                                    "JSON",
                                    `
{
  "data": {
    "auctionSellerPaidDealerReport": {
      "carfaxLink": {
        "url": "https://connect.carfax.com/dir?key=AVcsf..."
      },
      "expiresAt": {
        "unix": 1600265688,
        "unixMillis": 1600265688641,
        "rfc": "2020-09-16T14:14:48.641198Z"
      }
    }
  }
}`
                                )
                            ]}/>
                        </p>
                        <p>
                            The returned URL takes you to the report.

                            Auction Seller Paid partners will always get the requested report.
                            They can access a report for any VIN without a prior explicit purchase.
                        </p>
                        Alternatively, if you would like to retrieve CARFAX links in bulk and then cache them for up to
                        seven days,
                        you can use a batch query to retrieve up to 100 CARFAX links at once.
                        <p className={"text-center"}>
                            <img
                                src={SellerPaidBatch}
                                width="600px"
                                alt="Partner Portal: Applications Screen"
                            />
                        </p>
                        Example of a batch query:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `[
{"query":"query($vin: Vin!){  auctionSellerPaidDealerReport(vin: $vin) {    carfaxLink {      url      expiresAt {    unix   unixMillis    rfc      }    }   }}","variables":{"vin":"1N4AL3AP7FC214814"}},
{"query":"query($vin: Vin!){  auctionSellerPaidDealerReport(vin: $vin) {    carfaxLink {      url      expiresAt {    unix   unixMillis    rfc      }    }   }}","variables":{"vin":"1D7HA182X8S587373"}}
]`
                            )
                        ]}/>
                        <br/>
                        Response:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "JSON",
                                `[
{
  "data": {
    "auctionSellerPaidDealerReport": {
      "carfaxLink": {
        "url": "https://connect.carfax.com/dir?key=AVcsfgh123"
      },
      "expiresAt": {
        "unix": 1600265688,
        "unixMillis": 1600265688641,
        "rfc": "2020-09-16T14:14:48.641198Z"
      }
    }
  }
},
{
  "data": {
    "auctionSellerPaidDealerReport": {
      "carfaxLink": {
        "url": "https://connect.carfax.com/dir?key=BCAsdcc321"
      },
      "expiresAt": {
        "unix": 1600265688,
        "unixMillis": 1600265688641,
        "rfc": "2020-09-16T14:14:48.641198Z"
      }
    }
  }
}
]`
                            )
                        ]}/>
                    </p>
                </section>
                <hr/>
                <section>
                    <h3 id="seller-paid-snapshot">Snapshot</h3>
                    <p>
                        Snapshot is a simple html file generated for the client to give a
                        'snapshot' of key vehicle history information. When added to an
                        inventory management tool, Snapshot offers these benefits:
                    </p>
                    <ul>
                        <li>
                            For all vehicles already featuring a Dealer Instant CARFAX Report,
                            dealers may now see the same data found in the header of the
                            report at all places where they engage with the vehicle in your
                            application.
                        </li>
                        <li>
                            With just a hover over the CARFAX logo, dealers can quickly see
                            the key vehicle history points for the given vehicle, allowing
                            them to make timely inventory management decisions.
                        </li>
                        <li>
                            Dealers can reference the CARFAX History-Based Value for their
                            vehicles if your agreement terms provide for this added benefit.{" "}
                        </li>
                    </ul>
                    <img src={imgSnapshot} alt="speSnapshot Example" className={"img-fluid"} width="250px"/>
                    <p>
                        Unlike the legacy Snapshot, the new Snapshot URL has a
                        Snapshot key which hides the VIN and dealer information. Here's the query to get Snapshot Key.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `query ($vin: Vin!) {
    auctionSellerPaidDealerReport(vin: $vin) {
        snapshotKey
    }
}`
                            )
                        ]}/>
                    </p>
                    <p>
                        Variables:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify({vin: "JN8AF5MR5CT116133"}, null, 4)
                            )
                        ]}/>
                    </p>
                    <p>
                        Sample response:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify(
                                    {
                                        data: {
                                            auctionSellerPaidDealerReport: {
                                                snapshotKey:
                                                    "p5lI0fc6F52ATD-ayqZRUze_I30-6NQVyEahpAADvz4fcqX1kP6rNRvuA2JJBhC1ZDrE1dgeV7M1pBTGCWG3rVJDfY2aWSUvAmp0IpeC6tnf1oxHutKkb3s0jeUmrevT_GnG43aWAbyOlTlueofoE8fF",
                                            },
                                        },
                                    },
                                    null,
                                    4
                                )
                            )
                        ]}/>
                    </p>
                    <p>
                        Information such as <code>{`vin`}</code>, <code>{`userId`}</code> and{" "}
                        <code>{`dealerCompCode`}</code>
                        are embedded in the <code>{`snapshotKey`}</code> which can be used to
                        render the Snapshot data through the dealer Snapshot endpoint. The key
                        is used to ensure that the requests being made against our server are
                        authorized and can also be injected in an img or anchor tag to show
                        the Snapshot HTML.
                    </p>
                    <h4 id="including-snapshotjs">Including Snapshot.js</h4>
                    <p>
                        When implementing Snapshot, you'll need to use our hosted JavaScript
                        file:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                '<script type="text/javascript" src="https://snapshot.carfax.com/latest/dealer-snapshot.js"></script>'
                            )
                        ]}/>
                    </p>
                    <p>
                        Generally, it should be included right before you close out your body
                        tag in your HTML document.
                    </p>
                    <h4 id="dealer-snapshot-hover-over">Dealer Snapshot Hover-Over</h4>
                    <p>
                        On a desktop application, we recognize that most existing promotions
                        will show simply a CARFAX logo or icon. Hovering over the logo/icon
                        will cause the Snapshot to render just to the side of the logo/icon.
                        Clicking on the logo/icon should open the dealer report directly.{" "}
                    </p>
                    <p>
                        Applying the bindings for the Snapshot hover-over is as easy as adding
                        only two attributes on your html element that you would like to display
                        Snapshot, when hovered over. The elements are as follows:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                'class="carfax-dealer-snapshot-hover" data-snapshotkey="SnapshotKey"'
                            )
                        ]}/>
                    </p>
                    <p>
                        SnapshotKey = The key you will get back from the
                        Connect Dealer Report Response
                    </p>
                    <p>
                        For example, an Image tag that shows the report when hovered over
                        would follow this pattern.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                '<img src="photo.jpg" class="carfax-dealer-snapshot-hover" data-snapshotkey="SnapshotKey"/>'
                            )
                        ]}/>
                    </p>
                    <h4 id="embedded-dealer-snapshot">Embedded Dealer Snapshot</h4>
                    <p>
                        The Dealer Snapshot may also be embedded on any page of your
                        application for easy presentation of the CARFAX vehicle history.
                        <br/>
                        The embedded Snapshot displays just as the hover-over version,
                        however, it’s not triggered by hovering over an element, and instead,
                        it’s added to the page as it loads. Just like the hover-over version
                        of Snapshot, the embedded version also allows dealers to click “View
                        the CARFAX Report” in order to see the full report.
                    </p>
                    <p>
                        The class for the embedded Snapshot is “carfax-snapshot-embedded”. You
                        will want to include the same JavaScript library whether using the
                        embedded or pop-up version of Snapshot on a page. We also recommend
                        using a <code>{`<div>`}</code> element for the embedded Snapshot to
                        ensure everything is styled appropriately.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                'class="carfax-dealer-snapshot-embedded" data-snapshotkey="SnapshotKey"'
                            )
                        ]}/>
                    </p>
                    <p>
                        SnapshotKey = The key you will get back from the
                        Connect Dealer Report Response
                    </p>
                    <p>
                        For example, an Image tag that shows the report when hovered over
                        would follow this pattern.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.MARKUP,
                                "HTML",
                                '<div class="carfax-dealer-snapshot-embedded" data-snapshotkey="SnapshotKey"></div>'
                            )
                        ]}/>
                    </p>
                    <h4 id={"refreshing snapshot"}>Refreshing Snapshot</h4>
                    <p>
                        If you have vin data loaded after the page load has already happened, then you should
                        reload the Snapshot.js file so that any newly added snapshots show up correctly.
                        In order to do so, please trigger the 'refreshSnapshotEvent' after the Connect data has
                        finished loading. This action will enable the proper viewing of all the snapshots on the page.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JAVASCRIPT,
                                "JavaScript",
                                `const refreshSnapshotEvent = new Event('refreshCarfaxSnapshotData');

document.dispatchEvent(refreshSnapshotEvent);`
                            )
                        ]}/>
                    </p>
                    <h4 id="specs">Specs</h4>
                    <p>
                        Snapshot is built to be resized responsively but we do have the
                        following min/max sizes that we suggest you use when rendering
                        Snapshot:
                    </p>
                    <ul>
                        <li>
                            Minimum width = 250 pixels
                        </li>
                        <li>
                            Maximum width = 350 pixels (a user may enlarge it beyond this but
                            the rows will simply stretch)
                        </li>
                        <li>
                            Maximum height = 518 pixels if the Year/Make/Model shows on three
                            lines
                        </li>
                    </ul>
                    <p>
                        Please note that the final implementation of Snapshot in your
                        application shall be mutually agreed to and approved by your CARFAX
                        account manager prior to launch. We encourage you to share screen
                        shots and/or provide beta access to your application to facilitate
                        this process.
                    </p>
                    <h4 id="sorry-message">Sorry Message</h4>
                    <p>
                        Should the report be unavailable, the service be down or the request
                        not properly authenticated, the dealer will receive a “sorry” message
                        like the one below.
                        <br/>
                        <img src={imgSorry} alt="Sorry Page" width="300" height="390"/>
                    </p>
                </section>
                <hr/>

                <section>
                    <h3 id={"cc-vehicle-information"}>Vehicle Information</h3>
                    <p>
                        In GraphQL, the data for the queries is returned in the same order that it was requested.
                        Although there may be use cases where the final state of the data is not in the same order.
                        In that case, there will need to be some sort of explicit mapping for the requested data with
                        its response.
                        CARFAX Connect supports the vehicle field which can be used to get back basic Vehicle
                        information such as the VIN to map the requested data to its response:
                    </p>
                    <CodeSnippetTemplate codeSnippets={[
                        new CodeSnippet(
                            CodeSnippetLanguages.GRAPHQL,
                            "GraphQL",
                            `query {
    auctionPartnerSellerPaid($vin: Vin!) {
        vehicle {
            vin
        }
    }
}`
                        )
                    ]}/>
                    Variables:
                    <CodeSnippetTemplate codeSnippets={[
                        new CodeSnippet(
                            CodeSnippetLanguages.GRAPHQL,
                            "JSON",
                            `{
    "vin": "WBANU53568CT40260"
}`
                        )
                    ]}/>
                    Response:
                    <CodeSnippetTemplate codeSnippets={[
                        new CodeSnippet(
                            CodeSnippetLanguages.GRAPHQL,
                            "JSON",
                            `{
  "data": {
    "auctionSellerPaidDealerReport": {
      "vehicle": {
        "vin": "WBANU53568CT40260"
      }
    }
  }
}
`
                        )
                    ]}/>
                </section>
                <hr/>

                {/*Auction Alert - Overview*/}
                <section>
                    <h1> Auction Alert</h1>
                    <h3 id = {"overview"}>Overview</h3>
                    <p>
                        Auction Alert is designed with the dynamic auction environment in mind.  It provides a mechanism for auctions to retrieve mission critical CARFAX information on vehicles that are offered for sale.
                        The information that is returned is broken down into three critical components: </p>
                    <ul>
                        <li>
                            Exception Information
                        </li>
                        <li>
                            Odometer Discrepancies
                        </li>
                        <li>
                            Type of Use
                        </li>
                    </ul>
                    <p>
                        These three categories of information are designed to assist in an auction’s check-in process and to improve block announcements.
                        It is recommended that the CARFAX information is not cached as new information is added to the CARFAX Vehicle History Database every day.
                    </p>
                    <p>
                        The CARFAX Auction Alert product allows authorized partners to access the Vehicle History Database through CARFAX Connect.
                        Below are descriptions of the request syntax and parameters as well as possible responses.
                    </p>

                </section>
                <hr/>

                {/*Auction Alert - API Schema*/}
                <section>
                    <h3 id = {"api-schema-auction-alert"}>API Schema</h3>
                    <p>
                        For the purposes of this guide, the following Schema will be used:
                        <br/>
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `extend type Query {
    auctionAlert(vin: Vin!, auctionCode: String!, stockNumber: String!, saleDate: String!): JSON
} 

scalar RfcTimestamp

scalar Seconds

type Timestamp {
    # UNIX timestamp; seconds elapsed since the UNIX epoch, 1970-01-01T00:00:00Z, neglecting leap seconds
    unix: Seconds!
 
    # Milliseconds elapsed since the UNIX epoch, 1970-01-01T00:00:00Z, neglecting leap seconds
    unixMillis: Milliseconds!
 
    # Date-time string in RFC 3339/ISO 8601 format, such as '2019-02-01T13:22:33Z'
    rfc: RfcTimestamp!
} 

# An absolute URL, with encoding applied as needed
scalar Url 

type Vehicle {
    vin: Vin!
}

# Vehicle Identification Number; 17 uppercase alphanumeric characters
scalar Vin
 
`
                            )
                        ]}/>
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Prerequisites*/}
                <section>
                    <h3 id={"prerequisites-auction-alert"}>Prerequisites</h3>
                    <p>
                        Before you can authenticate and start running queries on CARFAX Connect, you must be configured by CARFAX for the Auction Alert service and have a <strong> Client Credentials </strong> Application set up in the Partner Portal.
                        You can choose to use the one we provided (App Type - Client Credentials), or use one that you create.
                    </p>
                    <p className={"text-center"}>
                        <img
                            src={PartnerPortalApplications}
                            width="647px"
                            alt="Partner Portal: Applications Screen"
                        />
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Authentication*/}
                <section>
                    <h3 id={"authentication"}>Authentication</h3>
                    <p>
                        To get started a lead developer will receive an invitation to the Connect Partner Portal as noted on the
                        <Link to={Constants.AUTHENTICATION_PAGE_URL + "#partner-portal"}> Authentication</Link> page.
                    </p>
                    <p>
                        Once you have access to the Partner Portal, create a Client Credentials application. With the Client Credentials application built, you next need to authenticate to receive an access token (JWT).
                        This token will allow you to make requests to CARFAX Connect in the form of GraphQL queries to receive Auction Alert information for a given VIN.
                    </p>
                    <p>
                        Refer to this documentation page for more information on how to establish the Client Credentials application and authenticate -
                        <Link to={Constants.AUTHENTICATION_PAGE_URL + "#client-credentials-flow"}> Client Credentials Authentication</Link>
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Access Token*/}
                <section>
                    <h3 id={"step-1-access-token-auction-alert"}>Step 1 - Get an Access Token</h3>
                    <p>
                        CARFAX Connect follows the OAuth 2.0 authentication process. You'll be using the Client Credentials flow to get an Access Token.
                        Use your Client ID and Client Secret to retrieve an access token with an <code>HTTP POST</code> request to <code>https://auth.carfax.com/oauth/token</code>.
                        You can use the following cURL to request an access token.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(CodeSnippetLanguages.MARKUP, 'cURL',
                                `curl -XPOST \\
    -H "Content-type: application/json" \\
    -d '{"client_id":"YOUR_CLIENT_ID","client_secret":"YOUR_CLIENT_SECRET","audience":"https://connect.carfax.com","grant_type":"client_credentials"}' 'https://auth.carfax.com/oauth/token'
`)
                        ]}/>
                    </p>
                    <p>
                        The response will be similar to the response below. As you can see, your access token is under the access_token field.
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(CodeSnippetLanguages.JSON, 'JSON',
                                JSON.stringify(
                                    {
                                        "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9....",
                                        "expires_in": 86400,
                                        "token_type": "Bearer"
                                    }, null, 4
                                ))
                        ]}/>
                    </p>
                    <p>
                        <strong>IMPORTANT</strong>: An access token once obtained is valid for 24 hours and should be reused until it expires.
                        Please do not fetch a new access token with each request. If coded properly we would expect to see one refresh per day if calling from a single machine or perhaps as many as 10 if using distributed machines to call Connect.
                        The expiration is identified via expires_in which is in seconds. Be sure to obtain a fresh token sufficiently far in advance to allow for network delays and clock skew.
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - GraphQL Query*/}
                <section>
                    <h3 id={"step-2-graphql-query-auction-alert"}>Step 2 - Prepare the GraphQL Query</h3>
                    <p>
                        For the purposes of this guide, we will use the following GraphQL query:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.GRAPHQL,
                                "GraphQL",
                                `
query{
  auctionAlert(vin: "1MEFM53S9WG646595", auctionCode: "TESTAUC", stockNumber: "TESTAUC1234",saleDate: "10/20/2023")
}
`
                            )
                        ]}/>
                    </p>
                    <p>
                        Convert your GraphQL to a JSON object. You can also use commas(,) or spaces to separate the different types in the query instead of the newline character(\n).
                        GraphQL is very flexible when it comes to how you want to format the query if readability is important:
                    </p>
                    <CodeSnippetTemplate codeSnippets={[
                        new CodeSnippet(CodeSnippetLanguages.JSON, 'JSON',
                            JSON.stringify({"query":"query{  auctionAlert(vin: \"1MEFM53S9WG646595\",auctionCode: \"TESTAUC\",stockNumber: \"TESTAUC1234\",saleDate: \"10/20/2023\")}"}, null, 4))
                    ]}/>
                    <p>
                        For more information on sending GraphQL queries, and what type of queries CARFAX Connect supports, please refer to
                        <Link to={Constants.SEND_QUERIES_PAGE_URL}> Sending Queries page</Link>.
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Sending Request*/}
                <section>
                    <h3 id={"step-3-send-request-auction-alert"}>Step 3 - Sending a request to CARFAX Connect</h3>
                    <p>
                        Now you are ready to make a request to CARFAX Connect for Auction Alert.
                        First, ensure that the following required parameters are available:
                        <table className="table table-striped table-bordered">
                            <thead>
                            <tr>
                                <th>Parameter</th>
                                <th>Length</th>
                                <th>Description</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td>VIN</td>
                                <td>17 characters, alphanumeric</td>
                                <td>A 17-character Vehicle Identification Number.</td>
                            </tr>
                            <tr>
                                <td>Auction Code</td>
                                <td>1-50 characters alphanumeric</td>
                                <td>A code used to designate a specific auction and authenticate the request.  Supplied by the auction.
                                    <strong> IMPORTANT</strong> - This code must be uniquely assigned to a given auction.</td>
                            </tr>
                            <tr>
                                <td>Sale Date</td>
                                <td>10-characters formatted as MM/DD/YYYY.</td>
                                <td>Date of the auction sale for which the information is being requested.</td>
                            </tr>
                            <tr>
                                <td>Stock Number</td>
                                <td>1-50 characters alphanumeric</td>
                                <td>Tracking code used by the auction to identify their specific vehicle for a specific sale.</td>
                            </tr>
                            </tbody>
                        </table>
                    </p>
                    <p>
                        Then prepare a new <code>HTTP POST</code> request to <code>http://connect.carfax.com/v1/graphql</code> . Paste the access token from the first step into the header of the HTTP request.
                        Don't forget to put "Bearer" and then a space before the access token. In the body of the request to CARFAX Connect, write your GraphQL query in JSON format from the previous step.
                        Example below shows the whole request for querying a CARFAX link in cURL format:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(CodeSnippetLanguages.MARKUP, 'cURL',
                                `curl --request POST \\
  --url https://connect.carfax.com/v1/graphql \\
  --header 'authorization: Bearer <YOUR_ACCESS_TOKEN_HERE>' \\
  --header 'content-type: application/json' \\
  --data '{"query":"query{  auctionAlert(vin: \\"1MEFM53S9WG646595\\",auctionCode: \\"TESTAUC\\",stockNumber: \\"TESTAUC1234\\",saleDate: \\"10/20/2023\\")}"}'`)
                        ]}/>
                    </p>
                    <p>
                        If you use the exact same query as the example above, the response you receive would be like:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [
           {
             "displayText": "JUNK TITLE",
             "city": "Fort Wayne",
             "state": "IN",
             "date": "08/24/2018"
           }
        ],
        "odometer-discrepancies": [],
        "typesOfUse": {
         "typeOfUse": []
        },
        "last-reported-odometer-reading": 127469
      },
      "decode": {
        "make": "MERCURY",
        "model": "SABLE LS PREMIUM",
        "year": "1998"
      },
      "request-info": {
        "vin": "1MEFM53S9WG646595",
        "auctionCode": "TESTAUC",
        "stockNumber": "TESTSHIKHA1234",
        "saleDate": "10/20/2023"
      }
    }
  }
}
`
                            )
                        ]}/>
                    </p>
                    <p>
                        Now you are all set to use your Client Credentials and query Connect for Auction Alert results. For more information about the authentication, check out the
                        <Link to={Constants.AUTHENTICATION_PAGE_URL + "#client-credentials-flow"}> Client Credentials authentication flow</Link>.
                        Auction Alert may be queried for a single VIN or in batches as outlined in the <Link to={Constants.SEND_QUERIES_PAGE_URL + "#batch-requests"}> Batch Requests </Link>page.
                    </p>
                    <p>
                        Example of a batch query:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify([
                                    {"query":"query{  auctionAlert(vin: \"1MEFM53S9WG646595\",auctionCode: \"TESTAUC\",stockNumber: \"TESTAUC1234\",saleDate: \"10/20/2023\")}"},
                                    {"query":"query{  auctionAlert(vin: \"1MEFM53S9WG646595\",auctionCode: \"TESTAUC\",stockNumber: \"TESTAUC1234\",saleDate: \"10/20/2023\")}"}
                                ], null, 4)
                            )
                        ]}/>
                    </p>
                    <p>
                        Batch response:
                        <CollapsibleContent content={
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify([
                                    {
                                        "data": {
                                          "auctionAlert": {
                                            "auction-alert": {
                                              "exceptions": [
                                                        {
                                                            "displayText": "JUNK TITLE",
                                                            "city": "Fort Wayne",
                                                            "state": "IN",
                                                            "date": "08/24/2018"
                                                        }
                                                    ],
                                                    "odometer-discrepancies": [],
                                                    "typesOfUse": {
                                                        "typeOfUse": []
                                                    },
                                                    "last-reported-odometer-reading": 127469
                                                },
                                                "decode": {
                                                    "make": "MERCURY",
                                                    "model": "SABLE LS PREMIUM",
                                                    "year": "1998"
                                                },
                                                "request-info": {
                                                    "vin": "1MEFM53S9WG646595",
                                                    "auctionCode": "TESTAUC",
                                                    "stockNumber": "TESTAUC1234",
                                                    "saleDate": "10/20/2023"
                                                }
                                            }
                                        }
                                    },
                                    {
                                        "data": {
                                            "auctionAlert": {
                                                "auction-alert": {
                                                    "exceptions": [
                                                        {
                                                            "displayText": "JUNK TITLE",
                                                            "city": "Fort Wayne",
                                                            "state": "IN",
                                                            "date": "08/24/2018"
                                                        }
                                                    ],
                                                    "odometer-discrepancies": [],
                                                    "typesOfUse": {
                                                        "typeOfUse": []
                                                    },
                                                    "last-reported-odometer-reading": 127469
                                                },
                                                "decode": {
                                                    "make": "MERCURY",
                                                    "model": "SABLE LS PREMIUM",
                                                    "year": "1998"
                                                },
                                                "request-info": {
                                                    "vin": "1MEFM53S9WG646595",
                                                    "auctionCode": "TESTAUC",
                                                    "stockNumber": "TESTAUC1234",
                                                    "saleDate": "10/20/2023"
                                                }
                                            }
                                        }
                                    }
                                ], null, 4)
                            )
                        ]}/>}/>
                    </p>

                </section>
                <hr/>

                {/*Auction Alert - Response Format*/}
                <section>
                    <h3 id={"response-format"}>Response Format</h3>
                    <p>
                        There are five primary sections to the response:
                    </p>
                    <ul>
                        <li>
                            <strong>Auction Alert</strong> - This is the core response and includes the results of submitting the VIN.  This section details any Vehicle History exceptions found, odometer discrepancies, and how the vehicle was used.
                        </li>
                        <li>
                            <strong>Decode</strong> - This section decodes the VIN and includes the vehicle year, make and model.
                        </li>
                        <li>
                            <strong>Error Messages</strong> - Any errors related to the request which may include VIN validity, invalid auction code, incomplete request, or system-level information.
                        </li>
                        <li>
                            <strong>Last Reported Odometer</strong>- This section provides the last reported odometer reading for the vehicle when available.
                        </li>
                        <li>
                            <strong>Request Info</strong> - The information that was submitted as a part of the request including the auction code, sale date, vehicle stock number and VIN.
                        </li>
                    </ul>
                    <p>
                        Each of the sections is detailed below for reference.
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Auction Alert Response*/}
                <section>
                    <h3 id={"auction-alert-response"}>Auction Alert Response</h3>
                    <p>
                        This is the core response and includes the results of submitting the VIN to the CARFAX Vehicle History database to identify these three valuable data sets:
                    </p>
                    <ol>
                        <li>
                            <strong>Exceptions</strong> - CARFAX Vehicle History information is provided within the exception section to build better block announcements.
                            Exceptions may include branded records, damage disclosure, total loss, structural damage, open recalls, airbag deployment, previous Canadian, currently stolen, recovered theft and scrapped.
                        </li>
                        <li>
                            <strong>Odometer Discrepancies</strong> - Provides detailed information on specific odometer discrepancy events.
                            Odometer discrepancies may include potential rollover, odometer problem, potential rollback, mileage inconsistency and verified rollback.
                        </li>
                        <li>
                            <strong>Type of Use</strong> - Provides information reported to CARFAX on how a vehicle was previously used.
                            Type of use may include commercial, rental, taxi, corporate, government, non-profit, taxi and police.
                        </li>
                    </ol>
                    <h4>Response with Exceptions</h4>
                    <p>
                        Every exception includes:
                    </p>
                    <ul>
                        <li>
                            city
                        </li>
                        <li>
                            date of the event
                        </li>
                        <li>
                            display text (exception description)
                        </li>
                        <li>
                            state
                        </li>
                    </ul>
                    <p>
                        NOTE: In the case where a data element is not available it will be identified with Null.
                    </p>
                    <p>
                        If there are exceptions, Connect returns a response like the example below.
                        Note that if there are multiple exceptions of the same type only the most recent record will be returned.
                        <CollapsibleContent content={
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [
          {
            "displayText": "SALVAGE TITLE",
            "city": null,
            "state": "KY",
            "date": "03/31/2003"
          }
        ],
        "odometer-discrepancies": [],
        "typesOfUse": {
           "typeOfUse": []
        },
        "last-reported-odometer-reading": "No Last Reported Odometer Reading Found"
      },
      "decode": {
        "make": "HONDA",
        "model": "CBR600F4",
        "year": "2000"
      },
      "request-info": {
        "vin": "JH2PC3502YM104551",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>}/>
                    </p>
                    <h4> Response without Exceptions</h4>
                    <p>
                        If there are no exceptions, Connect returns a "No Exception Conditions Found on MM/DD/YYYY" message as the status.
                        Example below:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
        "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [],
        "typesOfUse": {
          "typeOfUse": []
        },
        "last-reported-odometer-reading": 131924
      },
      "decode": {
        "make": "HONDA",
        "model": "CIVIC EX",
        "year": "2000"
      },
      "request-info": {
        "vin": "1HGEJ8246YL006494",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>
                    </p>
                    <h4>Response with an Odometer Discrepancy</h4>
                    <p>
                        When an odometer discrepancy is found, the data that is returned is divided into three sub-sections:
                    </p>
                    <ol>
                        <li>
                            prior record
                        </li>
                        <li>
                            problem record
                        </li>
                        <li>
                            subsequent record
                        </li>
                    </ol>
                    <p>
                        Each section contains:
                    </p>
                    <ul>
                        <li>
                            date of the record
                        </li>
                        <li>
                            odometer reading of the record
                        </li>
                        <li>
                            source / type of record
                        </li>
                    </ul>
                    <p>
                        The problem record contains a description related to the nature of the discrepancy such as “Potential Odometer Rollback.”
                        <CollapsibleContent content={
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
          "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [
          {
            "prior-record": {
              "date": "09/30/1998",
              "source": "California Inspection Station",
              "odometer-reading": 83379
            },
            "problem-record": {
              "date": "11/03/2000",
              "source": "California Inspection Station",
              "odometer-reading": 1016,
              "display-text": "Potential odometer rollback"
            },
            "subsequent-record": {
              "date": "05/13/2002",
              "source": "California Inspection Station",
              "odometer-reading": 5506
            }
          }
        ],
        "typesOfUse": {
          "typeOfUse": []
        },
        "last-reported-odometer-reading": 110000
      },
      "decode": {
        "make": "MITSUBISHI",
        "model": "MIGHTY MAX SP",
        "year": "1984"
      },
      "request-info": {
        "vin": "JA7FK44E0EY401488",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>}/>
                    </p>
                    <p>
                        If an odometer discrepancy is found and there is neither a prior or subsequent record, the record includes a specific status message:
                        <CollapsibleContent content={
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
        "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [
          {
            "prior-record": {
              "status": "No Prior Record Found"
            },
            "problem-record": {
              "date": "07/26/1994",
              "source": "Virginia Motor Vehicle Dept.",
              "odometer-reading": 146851,
              "display-text": "Mileage inconsistency"
            },
            "subsequent-record": {
              "date": "06/13/1995",
              "source": "Virginia Motor Vehicle Dept.",
              "odometer-reading": 142305
            }
          },
          {
            "prior-record": {
              "date": "06/13/1995",
              "source": "Virginia Motor Vehicle Dept.",
              "odometer-reading": 142305
            },
            "problem-record": {
              "date": "03/07/2003",
              "source": "Fleet Management Co.",
              "odometer-reading": 121273,
              "display-text": "Mileage inconsistency"
            },
            "subsequent-record": {
              "status": "No Subsequent Record Found"
            }
          }
        ],
        "typesOfUse": {
          "typeOfUse": []
        },
        "last-reported-odometer-reading": 121273
      },
      "decode": {
         "make": "FORD",
         "model": "F150",
         "year": "1985"
      },
      "request-info": {
         "vin": "1FDCF1590FPB15508",
         "auctionCode": "TESTAUC",
         "stockNumber": "23453",
         "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>}/>
                    </p>
                    <p>
                        If multiple discrepancies are found they are included within the odometer-discrepancies element:
                        <CollapsibleContent content={
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
        "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [
          {
            "prior-record": {
              "status": "No Prior Record Found"
            },
            "problem-record": {
              "date": "07/26/1994",
              "source": "Virginia Motor Vehicle Dept.",
              "odometer-reading": 146851,
              "display-text": "Mileage inconsistency"
            },
            "subsequent-record": {
              "date": "06/13/1995",
              "source": "Virginia Motor Vehicle Dept.",
              "odometer-reading": 142305
            }
          },
          {
            "prior-record": {
              "date": "06/13/1995",
              "source": "Virginia Motor Vehicle Dept.",
              "odometer-reading": 142305
            },
            "problem-record": {
              "date": "03/07/2003",
              "source": "Fleet Management Co.",
              "odometer-reading": 121273,
              "display-text": "Mileage inconsistency"
            },
            "subsequent-record": {
              "status": "No Subsequent Record Found"
            }
          }
        ],
        "typesOfUse": {
          "typeOfUse": []
        },
        "last-reported-odometer-reading": 121273
      },
      "decode": {
        "make": "FORD",
        "model": "F150",
        "year": "1985"
      },
      "request-info": {
        "vin": "1FDCF1590FPB15508",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>}/>
                    </p>
                    <p>
                        Note that only the last reported record is supplied for Verified Rollbacks and Odometer Problems.  No prior nor subsequent records are returned in these cases.
                    </p>
                    <h4>Response without an Odometer Discrepancy</h4>
                    <p>
                        If no discrepancies are found, an empty array/brackets [ ] are returned.
                    </p>
                    <h4>Response with Type of Use</h4>
                    <p>
                        A short description appears within this sub-section, and is displayed as follows:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
        "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [],
        "typesOfUse": {
          "typeOfUse": [
            "Corporate"
          ]
        },
        "last-reported-odometer-reading": 80251
      },
      "decode": {
        "make": "CHEVROLET",
        "model": "C2500 SUBURBAN",
        "year": "1995"
      },
      "request-info": {
        "vin": "1GBGC26K8SJ312255",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>
                    </p>
                    <p>
                        If multiple types of use occur on a VIN, they are reflected in the following manner:
                        <CollapsibleContent content={
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [
          {
            "displayText": "THEFT",
            "city": null,
            "state": null,
            "date": "09/25/2020"
          },
          {
            "displayText": "OPEN RECALL",
            "city": null,
            "state": null,
            "date": "07/14/2020"
          }
        ],
        "odometer-discrepancies": [],
        "typesOfUse": {
          "typeOfUse": [
            "Commercial",
            "Taxi"
          ] 
        },
        "last-reported-odometer-reading": 384239
      },
      "decode": {
        "make": "DODGE",
        "model": "GRAND CARAVAN SE",
        "year": "2008"
      },
      "request-info": {
        "vin": "2D8HN44H18R761604",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>}/>
                    </p>
                    <h4>Response with no Type of Use</h4>
                    <p>
                        If no type of use is found, an empty array/brackets [ ] are returned.
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Decode*/}
                <section>
                    <h3 id={"decode"}>Decode</h3>
                    <p>
                        When a VIN is submitted, the vehicle year, make and model are decoded and returned.  That information is displayed as a part of the decode data element.
                        Any vehicle 1981 or later that follows the VIN decode standards should be able to be decoded.  The response may look like this:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
        "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [],
        "typesOfUse": {
          "typeOfUse": [
            "Corporate"
          ]
        },
        "last-reported-odometer-reading": 80251
      },
      "decode": {
        "make": "CHEVROLET",
        "model": "C2500 SUBURBAN",
        "year": "1995"
      },
      "request-info": {
        "vin": "1GBGC26K8SJ312255",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>
                    </p>
                    <p>
                        If a vehicle cannot be decoded, an “NA” appears after each element.  An example appears below:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
        "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [],
        "typesOfUse": {
          "typeOfUse": [
            "Corporate"
          ]
        },
        "last-reported-odometer-reading": 80251
      },
      "decode": {
        "make": "NA",
        "model": "NA",
        "year": "NA"
      },
      "request-info": {
        "vin": "1GBGC26K8SJ312255",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Error Messages*/}
                <section>
                    <h3 id={"error-messages"}>Error Messages</h3>
                    <p>
                        When an error occurs, a short description appears in the error message section.  Below is a list of all Auction Alert errors and how they will appear.  See
                        <Link to={Constants.ERROR_HANDLING_PAGE_URL}> Error Handling </Link>for more information.
                        <table className="table table-striped table-bordered">
                            <thead>
                            <tr>
                                <th>Code</th>
                                <th>Description</th>
                                <th>Displayable Error Message</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td>INVALID_PARTNER_ACCOUNT</td>
                                <td>Partner account is disabled or otherwise invalid</td>
                                <td>NA</td>
                            </tr>
                            <tr>
                                <td>GENERIC_SERVER_ERROR</td>
                                <td>There was an error. Please try again later.</td>
                                <td>Sorry, CARFAX is unavailable at this time. Please try again later.</td>
                            </tr>
                            <tr>
                                <td>UNAUTHORIZED_USER</td>
                                <td>User is not authorized to access this product</td>
                                <td>Not authorized to access this product.  Please contact CARFAX for details.</td>
                            </tr>
                            <tr>
                                <td>MISSING_AUCTION_CODE</td>
                                <td>Auction Code was not included in the request</td>
                                <td>NA</td>
                            </tr>
                            <tr>
                                <td>MISSING_SALE_DATE</td>
                                <td>Sale Date was not included in the request</td>
                                <td>NA</td>
                            </tr>
                            <tr>
                                <td>MISSING_STOCK_NUMBER</td>
                                <td>Stock Number was not included in the request</td>
                                <td>NA</td>
                            </tr>
                            <tr>
                                <td>INVALID_AUCTION_CODE</td>
                                <td>Auction Code is incorrectly formatted</td>
                                <td>Incorrectly formatted Auction Code in request.  Please contact Customer Support at your auction software provider.</td>
                            </tr>
                            <tr>
                                <td>INVALID_SALE_DATE</td>
                                <td>Sale Date is incorrectly formatted; should be MM/DD/YYYY</td>
                                <td>Incorrectly formatted Sale Date in request.  Please contact Customer Support at your auction software provider.</td>
                            </tr>
                            <tr>
                                <td>INVALID_STOCK_NUMBER</td>
                                <td>Stock Number is incorrectly formatted</td>
                                <td>Incorrectly formatted Stock Number in request.  Please contact Customer Support at your auction software provider.</td>
                            </tr>
                            </tbody>
                        </table>
                    </p>
                    <p>
                        Example:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "errors": [
    {
      "message": "Exception while fetching data (/auctionAlert) : Sale Date is incorrectly formatted; should be MM/DD/YYYY",
      "locations": [
        {
          "line": 2,
          "column": 3
        }
      ],
      "path": [
        "auctionAlert"
      ],
      "extensions": {
        "displayableErrorMessage": "Sale Date is incorrectly formatted; should be MM/DD/YYYY",
        "requestId": "5a36e777-4efe-4e53-a273-e66f3e4371cc",
        "errorCode": "INVALID_SALE_DATE",
        "vin": "2D8HN44H18R761604",
        "classification": "DataFetchingException"
     }
   }
  ],
  "data": {
    "auctionAlert": null
  }
}
`
                            )
                        ]}/>
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Last Reported Odometer Reading*/}
                <section>
                    <h3 id={"last-reported-odometer-reading"}>Last Reported Odometer Reading</h3>
                    <p>
                        When a VIN is submitted, the vehicle's last reported odometer reading is always returned if available.  The response may look like this:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
        "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [],
        "typesOfUse": {
          "typeOfUse": []
        },
        "last-reported-odometer-reading": 131924
      },
      "decode": {
        "make": "HONDA",
        "model": "CIVIC EX",
        "year": "2000"
      },
      "request-info": {
        "vin": "1HGEJ8246YL006494",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>
                    </p>
                    <p>
                        If a vehicle's last reported odometer reading is not available "No Last Reported Odometer Reading Found" is returned.
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Request Info*/}
                <section>
                    <h3 id={"request-info"}>Request Info</h3>
                    <p>
                        This section echoes back the information that was submitted as part of the original request.  The four fields included in this section are the auction code, the auction sale date, the stock number for the vehicle and the VIN.
                        Each of these fields is required in order for CARFAX to fulfill the information request.  The auction code and stock number vary depending on the auction location.  The sale date is the planned date of the auction, not necessarily the date the transaction takes place.
                        The request info section may look like this:
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(
                                CodeSnippetLanguages.JSON,
                                "JSON",
                                `
{
  "data": {
    "auctionAlert": {
      "auction-alert": {
        "exceptions": [],
        "exceptionStatus": "No Exception Conditions Found on 10/27/2022",
        "odometer-discrepancies": [],
        "typesOfUse": {
          "typeOfUse": []
        },
        "last-reported-odometer-reading": 131924
      },
      "decode": {
        "make": "HONDA",
        "model": "CIVIC EX",
        "year": "2000"
      },
      "request-info": {
        "vin": "1HGEJ8246YL006494",
        "auctionCode": "TESTAUC",
        "stockNumber": "23453",
        "saleDate": "10/27/2022"
      }
    }
  }
}
`
                            )
                        ]}/>
                    </p>
                </section>
                <hr/>

                {/*Auction Alert - Additional Support*/}
                <section>
                    <h3 id={"additional-support"}>Additional Support</h3>
                    <p>
                        Please contact your CARFAX account manager regarding any questions related to account access, submitting data corrections or further opportunities for improved data sharing.
                        Contact <a href="mailto:DSDataManagement@carfax.com">DSDataManagement@carfax.com</a> regarding any questions related to the transfer of auction sale data including file structure and FTP site access.
                    </p>
                </section>



            </div>
        )
    }
}
